import { useEffect, useMemo, useState } from "react";
import Modal from "../../shared/components/Modal";
import { campo, etiqueta, fechaLarga } from "../../shared/estilos";
import { avisar, confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";
import { accionCuenta, personasSinCuenta } from "./cuentasService";

const SIN_ROL = "sin_rol";

const nombreDe = (u) => `${u.nombres || ""} ${u.apellidos || ""}`.trim();

// Agrupa las cuentas por rol: primero los de acceso total, luego por nombre.
export function agruparPorRol(usuarios, roles) {
  const peso = (r) => (r.administra_usuarios ? 2 : r.acceso_total ? 1 : 0);
  const ordenados = [...roles].sort((a, b) => peso(b) - peso(a) || a.nombre.localeCompare(b.nombre));
  const ids = new Set(roles.map((r) => r.id));
  const grupos = ordenados.map((rol) => ({ clave: rol.id, rol, usuarios: usuarios.filter((u) => u.rol_id === rol.id) }));
  const sinRol = usuarios.filter((u) => !ids.has(u.rol_id));
  if (sinRol.length) grupos.push({ clave: SIN_ROL, rol: null, usuarios: sinRol });
  return grupos;
}

function resumenAcceso(rol) {
  if (!rol) return "Asígnales un rol para definir qué ven";
  if (rol.administra_usuarios) return "Administrador";
  if (rol.acceso_total) return "Acceso total";
  const n = (rol.permisos || []).length;
  return n === 1 ? "1 sección" : `${n} secciones`;
}

function SelectorRol({ valor, roles, onCambio }) {
  return (
    <div>
      <label htmlFor="usuario-rol" className={etiqueta}>Rol *</label>
      <Selector id="usuario-rol" value={valor || ""} onChange={(e) => onCambio(e.target.value ? Number(e.target.value) : null)} className={campo}>
        <option value="">Elige un rol</option>
        {roles.map((r) => (
          <option key={r.id} value={r.id}>{r.nombre}{r.administra_usuarios ? " (administrador)" : r.acceso_total ? " (acceso total)" : ""}</option>
        ))}
      </Selector>
    </div>
  );
}

// Invitar: a una persona ya registrada en Miembros o a una nueva. Le llega un correo para crear su contraseña.
function Invitar({ rolInicial, roles, onListo, onCerrar }) {
  const [personas, setPersonas] = useState(null);
  const [nueva, setNueva] = useState(false);
  const [form, setForm] = useState({ rol_id: rolInicial || null, miembro_id: "", nombres: "", apellidos: "", email: "" });
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    personasSinCuenta()
      .then((lista) => {
        setPersonas(lista);
        if (!lista.length) setNueva(true);
      })
      .catch((e) => setError(e.message));
  }, []);

  const cambiar = (clave) => (e) => setForm({ ...form, [clave]: e.target.value });

  const enviar = async () => {
    setError("");
    if (!form.rol_id) return setError("Elige un rol");
    const datos = nueva
      ? { persona: { nombres: form.nombres, apellidos: form.apellidos, email: form.email } }
      : { miembro_id: Number(form.miembro_id) || null };
    if (!nueva && !datos.miembro_id) return setError("Elige a la persona");
    if (nueva && (!form.nombres.trim() || !form.apellidos.trim() || !form.email.includes("@"))) {
      return setError("Escribe nombres, apellidos y correo");
    }
    setEnviando(true);
    try {
      const resultado = await accionCuenta("invitar", { rol_id: form.rol_id, ...datos });
      if (resultado?.enviado === false) {
        avisar(`La cuenta de ${resultado.email} quedó creada, pero el correo no salió. Usa "Reenviar enlace".`, "error");
      } else {
        avisar(`Invitación enviada a ${resultado?.email}`, "exito");
      }
      onListo();
    } catch (e) {
      setError(e.message);
      setEnviando(false);
    }
  };

  return (
    <Modal titulo="Invitar a una persona" onCerrar={onCerrar}>
      <p className="mb-4 text-sm text-gray-400">Le llegará un correo para crear su contraseña. Entrará con ese correo.</p>
      <div className="mb-4 flex gap-2" role="group" aria-label="Quién">
        <button type="button" onClick={() => setNueva(false)} disabled={personas !== null && !personas.length}
          className={`pestana ${!nueva ? "pestana-activa" : ""}`}>Ya registrada</button>
        <button type="button" onClick={() => setNueva(true)} className={`pestana ${nueva ? "pestana-activa" : ""}`}>Persona nueva</button>
      </div>
      <div className="space-y-4">
        {nueva ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="invitar-nombres" className={etiqueta}>Nombres *</label>
                <input id="invitar-nombres" value={form.nombres} onChange={cambiar("nombres")} className={campo} />
              </div>
              <div>
                <label htmlFor="invitar-apellidos" className={etiqueta}>Apellidos *</label>
                <input id="invitar-apellidos" value={form.apellidos} onChange={cambiar("apellidos")} className={campo} />
              </div>
            </div>
            <div>
              <label htmlFor="invitar-correo" className={etiqueta}>Correo *</label>
              <input id="invitar-correo" type="email" inputMode="email" autoCapitalize="none" value={form.email}
                onChange={cambiar("email")} className={campo} placeholder="nombre@correo.com" />
            </div>
          </>
        ) : (
          <div>
            <label htmlFor="invitar-persona" className={etiqueta}>Persona *</label>
            <Selector id="invitar-persona" value={form.miembro_id} onChange={cambiar("miembro_id")} className={campo}>
              <option value="">{personas === null ? "Cargando..." : "Elige a la persona"}</option>
              {(personas || []).map((p) => (
                <option key={p.id} value={p.id}>{p.nombres} {p.apellidos} · {p.email}</option>
              ))}
            </Selector>
            <p className="mt-1 text-xs text-gray-500">Solo aparecen quienes aún no tienen cuenta.</p>
          </div>
        )}
        <SelectorRol valor={form.rol_id} roles={roles} onCambio={(rol_id) => setForm({ ...form, rol_id })} />
        {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
      </div>
      <div className="mt-6 flex gap-3">
        <button onClick={enviar} disabled={enviando} className="btn-primario flex-1">
          {enviando ? "Enviando..." : "Enviar invitación"}
        </button>
        <button onClick={onCerrar} className="btn-fantasma flex-1">Cancelar</button>
      </div>
    </Modal>
  );
}

// Editar: cambiar el rol o desactivar la cuenta (desactivada no puede entrar).
function EditarCuenta({ cuenta, roles, onListo, onCerrar }) {
  const [form, setForm] = useState({ rol_id: cuenta.rol_id, activo: cuenta.activo !== false });
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      await accionCuenta("actualizar", { usuario_id: cuenta.id, rol_id: form.rol_id, activo: form.activo });
      onListo();
    } catch (e) {
      setError(e.message);
      setGuardando(false);
    }
  };

  return (
    <Modal titulo="Editar cuenta" onCerrar={onCerrar}>
      <p className="font-semibold text-white">{nombreDe(cuenta)}</p>
      <p className="mb-5 text-sm text-gray-400">{cuenta.email}</p>
      <div className="space-y-4">
        <SelectorRol valor={form.rol_id} roles={roles} onCambio={(rol_id) => setForm({ ...form, rol_id })} />
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} className="h-4 w-4" />
          <span className="text-sm text-white">Cuenta activa (si la desactivas, no podrá entrar)</span>
        </label>
        {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
      </div>
      <div className="mt-6 flex gap-3">
        <button onClick={guardar} disabled={guardando || !form.rol_id} className="btn-primario flex-1">
          {guardando ? "Guardando..." : "Guardar"}
        </button>
        <button onClick={onCerrar} className="btn-fantasma flex-1">Cancelar</button>
      </div>
    </Modal>
  );
}

function Estado({ cuenta }) {
  if (!cuenta.activo) return <span className="insignia border border-zinc-500/30 bg-zinc-500/15 text-zinc-400">Inactiva</span>;
  if (cuenta.pendiente) return <span className="insignia border border-amber-500/30 bg-amber-500/15 text-amber-300">Invitación pendiente</span>;
  return <span className="insignia border border-emerald-500/30 bg-emerald-500/15 text-emerald-300">Activa</span>;
}

// Cuentas del sistema agrupadas por su rol, con buscador.
function UsuariosPorRol({ usuarios, roles, yoId, onCambio }) {
  const [cerrados, setCerrados] = useState(() => new Set());
  const [busqueda, setBusqueda] = useState("");
  const [invitando, setInvitando] = useState(null);
  const [editando, setEditando] = useState(null);

  const texto = busqueda.trim().toLowerCase();
  const grupos = useMemo(() => {
    const todos = agruparPorRol(usuarios, roles);
    if (!texto) return todos;
    const coincide = (u) => `${u.email} ${nombreDe(u)}`.toLowerCase().includes(texto);
    return todos.map((g) => ({ ...g, usuarios: g.usuarios.filter(coincide) })).filter((g) => g.usuarios.length);
  }, [usuarios, roles, texto]);

  const alternar = (clave) => {
    const siguiente = new Set(cerrados);
    siguiente.has(clave) ? siguiente.delete(clave) : siguiente.add(clave);
    setCerrados(siguiente);
  };

  const reenviar = async (u) => {
    try {
      await accionCuenta("reenviar", { usuario_id: u.id });
      avisar(`Enlace enviado a ${u.email}`, "exito");
    } catch (e) {
      avisar(e.message, "error");
    }
  };

  const eliminar = async (u) => {
    if (!(await confirmar(`¿Eliminar la cuenta de ${nombreDe(u)}? Ya no podrá entrar. Su ficha de miembro se conserva.`, { aceptar: "Eliminar" }))) return;
    try {
      await accionCuenta("eliminar", { usuario_id: u.id });
      onCambio();
    } catch (e) {
      avisar(e.message, "error");
    }
  };

  const terminar = (cerrar) => () => {
    cerrar(null);
    onCambio();
  };

  return (
    <div className="panel p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h3 className="titulo-panel">Usuarios del Sistema</h3>
        <button onClick={() => setInvitando({})} className="btn-primario btn-sm">Invitar</button>
      </div>
      <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} aria-label="Buscar usuario"
        placeholder="Buscar por nombre o correo..." className={`${campo} mb-4`} />
      {texto && grupos.length === 0 && <p className="text-sm text-gray-400">Ninguna cuenta coincide con la búsqueda.</p>}

      <div className="space-y-3">
        {grupos.map(({ clave, rol, usuarios: lista }) => {
          const abierto = Boolean(texto) || !cerrados.has(clave);
          return (
            <section key={clave} className="rounded-xl border border-gray-700">
              <button onClick={() => alternar(clave)} aria-expanded={abierto}
                className="flex w-full items-center justify-between gap-4 rounded-xl p-4 text-left transition hover:bg-oscuro">
                <span>
                  <span className="font-semibold text-white">{rol ? rol.nombre : "Sin rol asignado"}</span>
                  <span className="mt-1 block text-xs text-gray-400">
                    {lista.length === 1 ? "1 usuario" : `${lista.length} usuarios`} · {resumenAcceso(rol)}
                  </span>
                </span>
                <span className="text-sm text-gray-400" aria-hidden="true">{abierto ? "▲" : "▼"}</span>
              </button>
              {abierto && (
                <div className="space-y-2 px-4 pb-4">
                  {lista.length === 0 && <p className="text-sm text-gray-500">Aún no hay usuarios en este rol.</p>}
                  {lista.map((u) => (
                    <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-800 p-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white">{nombreDe(u)}</p>
                        <p className="mt-1 break-all text-xs text-gray-400">
                          {u.email}
                          {" · "}
                          {u.ultimo_ingreso
                            ? `Último ingreso: ${fechaLarga(String(u.ultimo_ingreso).slice(0, 10), { day: "numeric", month: "short", year: "numeric" })}`
                            : "Nunca ha entrado"}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Estado cuenta={u} />
                        <button onClick={() => setEditando(u)} className="btn-fantasma btn-sm">Editar</button>
                        {u.activo && <button onClick={() => reenviar(u)} className="btn-fantasma btn-sm">Reenviar enlace</button>}
                        {u.id !== yoId && <button onClick={() => eliminar(u)} className="btn-peligro btn-sm">Eliminar</button>}
                      </div>
                    </div>
                  ))}
                  {rol && (
                    <button onClick={() => setInvitando({ rol_id: rol.id })} className="btn-fantasma btn-sm">
                      Invitar a {rol.nombre}
                    </button>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {invitando && (
        <Invitar rolInicial={invitando.rol_id} roles={roles} onCerrar={() => setInvitando(null)} onListo={terminar(setInvitando)} />
      )}
      {editando && (
        <EditarCuenta cuenta={editando} roles={roles} onCerrar={() => setEditando(null)} onListo={terminar(setEditando)} />
      )}
    </div>
  );
}

export default UsuariosPorRol;
